// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * App Theme Context
 *
 * Manages application-wide color scheme/theme for Mantine components.
 * This controls dashboard colors, buttons, cards, and other UI elements
 * (excluding the header which is controlled by ThemeColorContext).
 *
 * Provides eight theme options:
 * - Default (original CarePoint theme with navy and teal)
 * - Serene Sage (earthy greens and neutrals)
 * - Olive Oasis (olive greens with terracotta accents)
 * - Pastel Paradise (soft lavender, blush pink, and muted sage)
 * - Spring Revival (fresh sage greens with cool grays)
 * - Modern Minty (warm cream, blush, sage green, and mauve)
 * - Sageberry Bliss (soft lavender purples with sage green)
 * - Rustic Retreat (earthy browns and olives with deep forest green)
 *
 * Preferences are persisted to localStorage (no PHI involved).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { JSX, ReactNode } from 'react';
import type { MantineColorsTuple, MantineThemeOverride } from '@mantine/core';

const STORAGE_KEY = 'carepoint-app-theme';
const STYLE_STORAGE_KEY = 'carepoint-app-style';
const DARK_MODE_TINT_STORAGE_KEY = 'carepoint-dark-mode-tint';
const DARK_MODE_BASE_STORAGE_KEY = 'carepoint-dark-mode-base';

export type AppThemeId = 'default' | 'serene-sage' | 'olive-oasis' | 'pastel-paradise' | 'spring-revival' | 'modern-minty' | 'sageberry-bliss' | 'rustic-retreat';
export type AppStyleId = 'default' | 'flat';
export type DarkModeTintId = 'enhanced' | 'medium' | 'gentle';
export type DarkModeBaseId = 'neutral' | 'blue';

export interface AppStyleDefinition {
  id: AppStyleId;
  name: string;
  description: string;
}

export interface DarkModeTintColors {
  // Background colors (from darkest to lightest)
  dark9: string; // Deepest background
  dark8: string; // Main background
  dark7: string; // Card/panel background
  dark6: string; // Elevated surfaces
  dark5: string; // Hover states
  dark4: string; // Borders
  // Text colors
  gray0: string; // Primary text
  gray3: string; // Secondary text
  gray5: string; // Muted text
}

export interface DarkModeTintDefinition {
  id: DarkModeTintId;
  name: string;
  description: string;
  // CSS variable values for this tint level - keyed by base type
  colors: {
    neutral: DarkModeTintColors;
    blue: DarkModeTintColors;
  };
}

export interface DarkModeBaseDefinition {
  id: DarkModeBaseId;
  name: string;
  description: string;
}

export const DARK_MODE_TINTS: DarkModeTintDefinition[] = [
  {
    id: 'enhanced',
    name: 'Enhanced',
    description: 'High contrast with deeper blacks',
    colors: {
      // Pure grey (neutral) - HSL(0, 0%, x%)
      neutral: {
        dark9: '#0a0a0a',  // HSL(0, 0%, 4%)
        dark8: '#121212',  // HSL(0, 0%, 7%)
        dark7: '#1a1a1a',  // HSL(0, 0%, 10%)
        dark6: '#242424',  // HSL(0, 0%, 14%)
        dark5: '#2e2e2e',  // HSL(0, 0%, 18%)
        dark4: '#3a3a3a',  // HSL(0, 0%, 23%)
        gray0: '#ffffff',
        gray3: '#e0e0e0',
        gray5: '#a0a0a0',
      },
      // Blue grey - HSL(210-212, 18-27%, x%)
      blue: {
        dark9: '#090b0e',  // HSL(210, 25%, 4%)
        dark8: '#10141a',  // HSL(212, 25%, 8%)
        dark7: '#171c24',  // HSL(211, 20%, 12%)
        dark6: '#1e2530',  // HSL(212, 23%, 15%)
        dark5: '#272f3d',  // HSL(211, 21%, 20%)
        dark4: '#3a4556',  // HSL(212, 20%, 28%)
        gray0: '#ffffff',
        gray3: '#d9e0e8',
        gray5: '#9aa8b8',
      },
    },
  },
  {
    id: 'medium',
    name: 'Medium',
    description: 'Balanced dark mode (default)',
    colors: {
      // Pure grey (neutral)
      neutral: {
        dark9: '#141414',  // HSL(0, 0%, 8%)
        dark8: '#1a1a1a',  // HSL(0, 0%, 10%)
        dark7: '#262626',  // HSL(0, 0%, 15%)
        dark6: '#2e2e2e',  // HSL(0, 0%, 18%)
        dark5: '#3b3b3b',  // HSL(0, 0%, 23%)
        dark4: '#525252',  // HSL(0, 0%, 32%)
        gray0: '#f8f9fa',
        gray3: '#dee2e6',
        gray5: '#adb5bd',
      },
      // Blue grey
      blue: {
        dark9: '#141517',  // Original medium values (already blue-ish)
        dark8: '#1a1b1e',
        dark7: '#25262b',
        dark6: '#2c2e33',
        dark5: '#373a40',
        dark4: '#4d4f56',
        gray0: '#f8f9fa',
        gray3: '#dee2e6',
        gray5: '#adb5bd',
      },
    },
  },
  {
    id: 'gentle',
    name: 'Gentle',
    description: 'Softer, easier on the eyes',
    colors: {
      // Pure grey (neutral)
      neutral: {
        dark9: '#1f1f1f',  // HSL(0, 0%, 12%)
        dark8: '#292929',  // HSL(0, 0%, 16%)
        dark7: '#333333',  // HSL(0, 0%, 20%)
        dark6: '#404040',  // HSL(0, 0%, 25%)
        dark5: '#4d4d4d',  // HSL(0, 0%, 30%)
        dark4: '#666666',  // HSL(0, 0%, 40%)
        gray0: '#f1f3f5',
        gray3: '#ced4da',
        gray5: '#909296',
      },
      // Blue grey
      blue: {
        dark9: '#1e2023',  // Original gentle values
        dark8: '#262a2e',
        dark7: '#2f3439',
        dark6: '#383e44',
        dark5: '#454c54',
        dark4: '#5c6470',
        gray0: '#f1f3f5',
        gray3: '#ced4da',
        gray5: '#909296',
      },
    },
  },
];

export const DARK_MODE_BASES: DarkModeBaseDefinition[] = [
  {
    id: 'neutral',
    name: 'Pure Grey',
    description: 'Clean neutral greys without color tint',
  },
  {
    id: 'blue',
    name: 'Blue Grey',
    description: 'Subtle blue tint for a softer look',
  },
];

export const APP_STYLES: AppStyleDefinition[] = [
  {
    id: 'default',
    name: 'Default',
    description: 'Standard look with background colors on panels and cards',
  },
  {
    id: 'flat',
    name: 'Flat',
    description: 'Clean, paperlike look with transparent backgrounds',
  },
];

export interface AppThemeDefinition {
  id: AppThemeId;
  name: string;
  description: string;
  colors: {
    primary: MantineColorsTuple;
    secondary: MantineColorsTuple;
    accent: MantineColorsTuple;
    neutral: MantineColorsTuple;
    success: MantineColorsTuple;
    warning: MantineColorsTuple;
    error: MantineColorsTuple;
  };
  primaryColorName: string;
  previewColors: string[]; // 5 colors for the preview swatch
}

// Default CarePoint theme (original colors from main.tsx)
const DEFAULT_THEME: AppThemeDefinition = {
  id: 'default',
  name: 'Default',
  description: 'Original CarePoint theme with navy and teal accents',
  colors: {
    primary: [
      '#e8eef4',
      '#d1dde9',
      '#a3bbcf',
      '#7599b5',
      '#4a789d',
      '#3d5a80', // Primary navy
      '#364f70',
      '#2f4460',
      '#283950',
      '#212e40',
    ],
    secondary: [
      '#e6f7f5',
      '#ccefeb',
      '#99dfd7',
      '#66cfc3',
      '#33bfaf',
      '#0fb5a5', // Primary teal
      '#0da394',
      '#0b9183',
      '#097f72',
      '#076d61',
    ],
    accent: [
      '#f3f0f7',
      '#e7e1ef',
      '#cfc3df',
      '#b7a5cf',
      '#9f87bf',
      '#8b6fb0', // Soft purple
      '#7d639e',
      '#6f578c',
      '#614b7a',
      '#533f68',
    ],
    neutral: [
      '#f8f9fa',
      '#f1f3f5',
      '#e9ecef',
      '#dee2e6',
      '#ced4da',
      '#adb5bd',
      '#6c757d',
      '#495057',
      '#343a40',
      '#212529',
    ],
    success: [
      '#e6f7ef',
      '#ccefdf',
      '#99dfbf',
      '#66cf9f',
      '#33bf7f',
      '#10b981',
      '#0ea573',
      '#0c9165',
      '#0a7d57',
      '#086949',
    ],
    warning: [
      '#fef7e6',
      '#fdefcc',
      '#fbdf99',
      '#f9cf66',
      '#f7bf33',
      '#f59e0b',
      '#dd8e0a',
      '#c47e09',
      '#ac6e08',
      '#945e07',
    ],
    error: [
      '#fef2f2',
      '#fee2e2',
      '#fecaca',
      '#fca5a5',
      '#f87171',
      '#ef4444',
      '#dc2626',
      '#b91c1c',
      '#991b1b',
      '#7f1d1d',
    ],
  },
  primaryColorName: 'navy',
  previewColors: ['#3d5a80', '#0fb5a5', '#8b6fb0', '#adb5bd', '#10b981'],
};

// Serene Sage theme - Calming sage greens with warm neutrals
// Based on palette: #F1EAD8, #D5C7AD, #8A8E75, #BEC5A4, #68604D
const SERENE_SAGE_THEME: AppThemeDefinition = {
  id: 'serene-sage',
  name: 'Serene Sage',
  description: 'Calming sage greens with warm cream and earth tones',
  colors: {
    primary: [
      '#f4f5f1', // Lightest sage
      '#e8eae2',
      '#d1d5c5',
      '#bac0a8',
      '#a3ab8b',
      '#8A8E75', // Primary sage green
      '#7a7e67',
      '#6a6e59',
      '#5a5e4b',
      '#4a4e3d',
    ],
    secondary: [
      '#f7f5f0',
      '#eeebe1',
      '#ddd7c6',
      '#ccc3ab',
      '#bbaf90',
      '#D5C7AD', // Warm tan
      '#c0b39b',
      '#ab9f89',
      '#968b77',
      '#817765',
    ],
    accent: [
      '#f8f6f2',
      '#f1ead8', // Cream (from palette)
      '#e5dcc4',
      '#d9ceb0',
      '#cdc09c',
      '#BEC5A4', // Light sage (from palette)
      '#aab193',
      '#969d82',
      '#828971',
      '#6e7560',
    ],
    neutral: [
      '#f5f4f2',
      '#ebeae6',
      '#d7d6d0',
      '#c3c2ba',
      '#afaea4',
      '#9b9a8e',
      '#7a7970',
      '#68604D', // Dark earth (from palette)
      '#4a453c',
      '#2c2a25',
    ],
    success: [
      '#eef5ec',
      '#ddebd9',
      '#bbd7b3',
      '#99c38d',
      '#77af67',
      '#5a9b4a',
      '#4e8740',
      '#427336',
      '#365f2c',
      '#2a4b22',
    ],
    warning: [
      '#fdf6e8',
      '#fbedd1',
      '#f7dba3',
      '#f3c975',
      '#efb747',
      '#d9a032',
      '#c18c2a',
      '#a97822',
      '#91641a',
      '#795012',
    ],
    error: [
      '#fdf2f1',
      '#fbe5e3',
      '#f7cbc7',
      '#f3b1ab',
      '#ef978f',
      '#c9726a',
      '#b0635c',
      '#97544e',
      '#7e4540',
      '#653632',
    ],
  },
  primaryColorName: 'sage',
  previewColors: ['#F1EAD8', '#D5C7AD', '#8A8E75', '#BEC5A4', '#68604D'],
};

// Olive Oasis theme - Olive greens with terracotta and slate accents
// Based on palette: #757F64, #CB7A5C, #E9E2D8, #C7CDBF, #5C757A
const OLIVE_OASIS_THEME: AppThemeDefinition = {
  id: 'olive-oasis',
  name: 'Olive Oasis',
  description: 'Rich olive greens with warm terracotta and cool slate accents',
  colors: {
    primary: [
      '#f2f3f0',
      '#e5e7e1',
      '#cbcfc3',
      '#b1b7a5',
      '#979f87',
      '#757F64', // Primary olive (from palette)
      '#677058',
      '#59614c',
      '#4b5240',
      '#3d4334',
    ],
    secondary: [
      '#fcf5f2',
      '#f9ebe5',
      '#f3d7cb',
      '#edc3b1',
      '#e7af97',
      '#CB7A5C', // Terracotta (from palette)
      '#b66d52',
      '#a16048',
      '#8c533e',
      '#774634',
    ],
    accent: [
      '#f1f2f3',
      '#e3e6e7',
      '#c7cdcf',
      '#abb4b7',
      '#8f9b9f',
      '#5C757A', // Slate blue-gray (from palette)
      '#51676b',
      '#46595c',
      '#3b4b4d',
      '#303d3e',
    ],
    neutral: [
      '#f8f7f5',
      '#f1efeb',
      '#E9E2D8', // Warm cream (from palette)
      '#d5cdc0',
      '#C7CDBF', // Light sage (from palette)
      '#a9b0a1',
      '#8a9183',
      '#6b7265',
      '#4c5347',
      '#2d3429',
    ],
    success: [
      '#eef5ec',
      '#ddebd9',
      '#bbd7b3',
      '#99c38d',
      '#77af67',
      '#5a9b4a',
      '#4e8740',
      '#427336',
      '#365f2c',
      '#2a4b22',
    ],
    warning: [
      '#fdf6e8',
      '#fbedd1',
      '#f7dba3',
      '#f3c975',
      '#efb747',
      '#d9a032',
      '#c18c2a',
      '#a97822',
      '#91641a',
      '#795012',
    ],
    error: [
      '#fdf2f1',
      '#fbe5e3',
      '#f7cbc7',
      '#f3b1ab',
      '#ef978f',
      '#c9726a',
      '#b0635c',
      '#97544e',
      '#7e4540',
      '#653632',
    ],
  },
  primaryColorName: 'olive',
  previewColors: ['#757F64', '#CB7A5C', '#E9E2D8', '#C7CDBF', '#5C757A'],
};

// Pastel Paradise theme - Soft lavender, blush, slate blue, and sage
// Based on palette: #C0B0C1, #E6C8C6, #859EAC, #97AC9F, #6E8175
const PASTEL_PARADISE_THEME: AppThemeDefinition = {
  id: 'pastel-paradise',
  name: 'Pastel Paradise',
  description: 'Soft lavender, blush pink, and muted sage tones',
  colors: {
    primary: [
      '#f5f2f5',
      '#ebe5eb',
      '#d7ccd8',
      '#c3b3c4',
      '#af9ab0',
      '#C0B0C1', // Primary lavender
      '#a99caa',
      '#928893',
      '#7b747c',
      '#646065',
    ],
    secondary: [
      '#fcf5f4',
      '#f9ebea',
      '#f3d7d5',
      '#edc3c0',
      '#e7afab',
      '#E6C8C6', // Blush pink
      '#cfb4b2',
      '#b8a09e',
      '#a18c8a',
      '#8a7876',
    ],
    accent: [
      '#f1f4f5',
      '#e3e9eb',
      '#c7d3d7',
      '#abbdc3',
      '#8fa7af',
      '#859EAC', // Slate blue
      '#758b98',
      '#657884',
      '#556570',
      '#45525c',
    ],
    neutral: [
      '#f5f6f5',
      '#ebecea',
      '#d7d9d5',
      '#c3c6c0',
      '#afb3ab',
      '#97AC9F', // Muted sage
      '#859a8d',
      '#73887b',
      '#617669',
      '#4f6457',
    ],
    success: [
      '#f0f5f2',
      '#e1ebe5',
      '#c3d7cb',
      '#a5c3b1',
      '#87af97',
      '#6E8175', // Dark sage (from palette)
      '#617367',
      '#546559',
      '#47574b',
      '#3a493d',
    ],
    warning: [
      '#fdf6e8',
      '#fbedd1',
      '#f7dba3',
      '#f3c975',
      '#efb747',
      '#d9a032',
      '#c18c2a',
      '#a97822',
      '#91641a',
      '#795012',
    ],
    error: [
      '#fdf2f2',
      '#fbe6e6',
      '#f7cdcd',
      '#f3b4b4',
      '#ef9b9b',
      '#c97272',
      '#b06464',
      '#975656',
      '#7e4848',
      '#653a3a',
    ],
  },
  primaryColorName: 'lavender',
  previewColors: ['#C0B0C1', '#E6C8C6', '#859EAC', '#97AC9F', '#6E8175'],
};

// Spring Revival theme - Fresh sage greens with cool gray and warm beige
// Based on palette: #939C81, #B8BD9F, #B3B8BC, #BAAF9D, #EBEBE1
const SPRING_REVIVAL_THEME: AppThemeDefinition = {
  id: 'spring-revival',
  name: 'Spring Revival',
  description: 'Fresh sage greens with cool grays and warm beige',
  colors: {
    primary: [
      '#f3f4f1',
      '#e7e9e3',
      '#cfd3c7',
      '#b7bdab',
      '#9fa78f',
      '#939C81', // Primary sage
      '#828b72',
      '#717a63',
      '#606954',
      '#4f5845',
    ],
    secondary: [
      '#f6f7f3',
      '#edefe7',
      '#dbdfcf',
      '#c9cfb7',
      '#b7bf9f',
      '#B8BD9F', // Light sage
      '#a5aa8f',
      '#92977f',
      '#7f846f',
      '#6c715f',
    ],
    accent: [
      '#f5f5f6',
      '#ebeced',
      '#d7d9db',
      '#c3c6c9',
      '#afb3b7',
      '#B3B8BC', // Cool gray
      '#9fa4a8',
      '#8b9094',
      '#777c80',
      '#63686c',
    ],
    neutral: [
      '#f7f6f4',
      '#efede8',
      '#dfdbd1',
      '#cfc9ba',
      '#bfb7a3',
      '#BAAF9D', // Warm beige
      '#a79c8c',
      '#94897b',
      '#81766a',
      '#6e6359',
    ],
    success: [
      '#f2f5f0',
      '#e5ebe1',
      '#cbd7c3',
      '#b1c3a5',
      '#97af87',
      '#7d9b6a',
      '#6e885d',
      '#5f7550',
      '#506243',
      '#414f36',
    ],
    warning: [
      '#fdf6e8',
      '#fbedd1',
      '#f7dba3',
      '#f3c975',
      '#efb747',
      '#d9a032',
      '#c18c2a',
      '#a97822',
      '#91641a',
      '#795012',
    ],
    error: [
      '#fdf2f1',
      '#fbe5e3',
      '#f7cbc7',
      '#f3b1ab',
      '#ef978f',
      '#c9726a',
      '#b0635c',
      '#97544e',
      '#7e4540',
      '#653632',
    ],
  },
  primaryColorName: 'spring',
  previewColors: ['#939C81', '#B8BD9F', '#B3B8BC', '#BAAF9D', '#EBEBE1'],
};

// Modern Minty theme - Warm cream, blush, sage green, slate, and mauve
// Based on palette: #ECE4D9, #E8D1C3, #B7BEA0, #ABB5B6, #B18B7E
const MODERN_MINTY_THEME: AppThemeDefinition = {
  id: 'modern-minty',
  name: 'Modern Minty',
  description: 'Warm cream and blush with sage green and earthy mauve',
  colors: {
    primary: [
      '#f3f4f0',
      '#e7e9e1',
      '#cfd3c3',
      '#b7bda5',
      '#9fa787',
      '#B7BEA0', // Primary sage green
      '#a3aa8f',
      '#8f967e',
      '#7b826d',
      '#676e5c',
    ],
    secondary: [
      '#fbf8f6',
      '#f7f1ed',
      '#efe3db',
      '#e7d5c9',
      '#dfc7b7',
      '#E8D1C3', // Warm blush
      '#d0bcaf',
      '#b8a79b',
      '#a09287',
      '#887d73',
    ],
    accent: [
      '#f6f3f2',
      '#ede7e5',
      '#dbcfcb',
      '#c9b7b1',
      '#b79f97',
      '#B18B7E', // Earthy mauve
      '#9e7c70',
      '#8b6d62',
      '#785e54',
      '#654f46',
    ],
    neutral: [
      '#f5f5f6',
      '#ebeced',
      '#d7d9db',
      '#c3c6c9',
      '#afb3b7',
      '#ABB5B6', // Slate gray
      '#979fa0',
      '#83898a',
      '#6f7374',
      '#5b5d5e',
    ],
    success: [
      '#f0f5ec',
      '#e1ebd9',
      '#c3d7b3',
      '#a5c38d',
      '#87af67',
      '#6a9b4a',
      '#5c8740',
      '#4e7336',
      '#405f2c',
      '#324b22',
    ],
    warning: [
      '#fdf6e8',
      '#fbedd1',
      '#f7dba3',
      '#f3c975',
      '#efb747',
      '#d9a032',
      '#c18c2a',
      '#a97822',
      '#91641a',
      '#795012',
    ],
    error: [
      '#fdf2f1',
      '#fbe5e3',
      '#f7cbc7',
      '#f3b1ab',
      '#ef978f',
      '#c9726a',
      '#b0635c',
      '#97544e',
      '#7e4540',
      '#653632',
    ],
  },
  primaryColorName: 'minty',
  previewColors: ['#ECE4D9', '#E8D1C3', '#B7BEA0', '#ABB5B6', '#B18B7E'],
};

// Sageberry Bliss theme - Soft lavender purples with sage green accents
// Based on palette: #B296CA, #CEB7D7, #E0DCD1, #ACB9AD, #81987F
const SAGEBERRY_BLISS_THEME: AppThemeDefinition = {
  id: 'sageberry-bliss',
  name: 'Sageberry Bliss',
  description: 'Soft lavender purples with sage green and warm cream tones',
  colors: {
    primary: [
      '#f5f1f8',
      '#ebe3f1',
      '#d7c7e3',
      '#c3abd5',
      '#af8fc7',
      '#B296CA', // Primary lavender purple
      '#9f85b5',
      '#8c74a0',
      '#79638b',
      '#665276',
    ],
    secondary: [
      '#f8f5fa',
      '#f1ebf5',
      '#e3d7eb',
      '#d5c3e1',
      '#c7afd7',
      '#CEB7D7', // Light lavender
      '#b9a4c2',
      '#a491ad',
      '#8f7e98',
      '#7a6b83',
    ],
    accent: [
      '#f5f6f5',
      '#ebecea',
      '#d7d9d5',
      '#c3c6c0',
      '#afb3ab',
      '#ACB9AD', // Sage green
      '#99a69a',
      '#869387',
      '#738074',
      '#606d61',
    ],
    neutral: [
      '#f8f7f6',
      '#f1efec',
      '#E0DCD1', // Warm cream (from palette)
      '#cdc7ba',
      '#bab2a3',
      '#a79d8c',
      '#8a8175',
      '#6d655e',
      '#504947',
      '#332d30',
    ],
    success: [
      '#f0f5f1',
      '#e1ebe3',
      '#c3d7c7',
      '#a5c3ab',
      '#87af8f',
      '#81987F', // Dark sage (from palette)
      '#728771',
      '#637663',
      '#546555',
      '#455447',
    ],
    warning: [
      '#fdf6e8',
      '#fbedd1',
      '#f7dba3',
      '#f3c975',
      '#efb747',
      '#d9a032',
      '#c18c2a',
      '#a97822',
      '#91641a',
      '#795012',
    ],
    error: [
      '#fdf2f2',
      '#fbe6e6',
      '#f7cdcd',
      '#f3b4b4',
      '#ef9b9b',
      '#c97272',
      '#b06464',
      '#975656',
      '#7e4848',
      '#653a3a',
    ],
  },
  primaryColorName: 'sageberry',
  previewColors: ['#B296CA', '#CEB7D7', '#E0DCD1', '#ACB9AD', '#81987F'],
};

// Rustic Retreat theme - Earthy browns and olives with deep forest green
// Based on palette: #96784E, #E6E3DE, #838264, #B5C1B8, #112917
const RUSTIC_RETREAT_THEME: AppThemeDefinition = {
  id: 'rustic-retreat',
  name: 'Rustic Retreat',
  description: 'Earthy browns and olive with deep forest green accents',
  colors: {
    primary: [
      '#f4f2ee',
      '#e9e5dd',
      '#d3cbbb',
      '#bdb199',
      '#a79777',
      '#96784E', // Primary brown/tan
      '#856b45',
      '#745e3c',
      '#635133',
      '#52442a',
    ],
    secondary: [
      '#f3f3f1',
      '#e7e7e3',
      '#cfcfc7',
      '#b7b7ab',
      '#9f9f8f',
      '#838264', // Olive (from palette)
      '#747359',
      '#65644e',
      '#565543',
      '#474638',
    ],
    accent: [
      '#e8ebe9',
      '#d1d7d3',
      '#bac3bd',
      '#a3afa7',
      '#8c9b91',
      '#B5C1B8', // Sage/silver (from palette)
      '#a2aea5',
      '#8f9b92',
      '#7c887f',
      '#69756c',
    ],
    neutral: [
      '#f9f9f8',
      '#f3f2f0',
      '#E6E3DE', // Warm off-white (from palette)
      '#d4d0c9',
      '#c2bdb4',
      '#afaa9f',
      '#8c877d',
      '#69645b',
      '#464139',
      '#231e17',
    ],
    success: [
      '#e6ebe7',
      '#cdd7cf',
      '#b4c3b7',
      '#9baf9f',
      '#829b87',
      '#112917', // Deep forest green (from palette)
      '#0f2414',
      '#0d1f11',
      '#0b1a0e',
      '#09150b',
    ],
    warning: [
      '#fdf6e8',
      '#fbedd1',
      '#f7dba3',
      '#f3c975',
      '#efb747',
      '#d9a032',
      '#c18c2a',
      '#a97822',
      '#91641a',
      '#795012',
    ],
    error: [
      '#fdf2f1',
      '#fbe5e3',
      '#f7cbc7',
      '#f3b1ab',
      '#ef978f',
      '#c9726a',
      '#b0635c',
      '#97544e',
      '#7e4540',
      '#653632',
    ],
  },
  primaryColorName: 'rustic',
  previewColors: ['#96784E', '#E6E3DE', '#838264', '#B5C1B8', '#112917'],
};

export const APP_THEMES: AppThemeDefinition[] = [
  DEFAULT_THEME,
  SERENE_SAGE_THEME,
  OLIVE_OASIS_THEME,
  PASTEL_PARADISE_THEME,
  SPRING_REVIVAL_THEME,
  MODERN_MINTY_THEME,
  SAGEBERRY_BLISS_THEME,
  RUSTIC_RETREAT_THEME,
];

// Generate Mantine theme override from app theme definition
export function generateMantineTheme(theme: AppThemeDefinition): MantineThemeOverride {
  return {
    primaryColor: theme.primaryColorName,
    colors: {
      [theme.primaryColorName]: theme.colors.primary,
      teal: theme.colors.secondary,
      violet: theme.colors.accent,
      gray: theme.colors.neutral,
      green: theme.colors.success,
      amber: theme.colors.warning,
      red: theme.colors.error,
      // Keep navy for backwards compatibility in default theme
      ...(theme.id === 'default' ? { navy: theme.colors.primary } : {}),
    },
  };
}

interface AppThemeContextValue {
  /** Current app theme */
  currentTheme: AppThemeDefinition;
  /** Current theme ID */
  themeId: AppThemeId;
  /** Set the app theme by ID */
  setTheme: (themeId: AppThemeId) => void;
  /** Get all available themes */
  availableThemes: AppThemeDefinition[];
  /** Get Mantine theme override for current theme */
  mantineThemeOverride: MantineThemeOverride;
  /** Current app style */
  currentStyle: AppStyleDefinition;
  /** Current style ID */
  styleId: AppStyleId;
  /** Set the app style by ID */
  setStyle: (styleId: AppStyleId) => void;
  /** Get all available styles */
  availableStyles: AppStyleDefinition[];
  /** Current dark mode tint */
  currentDarkModeTint: DarkModeTintDefinition;
  /** Current dark mode tint ID */
  darkModeTintId: DarkModeTintId;
  /** Set the dark mode tint by ID */
  setDarkModeTint: (tintId: DarkModeTintId) => void;
  /** Get all available dark mode tints */
  availableDarkModeTints: DarkModeTintDefinition[];
  /** Current dark mode base */
  currentDarkModeBase: DarkModeBaseDefinition;
  /** Current dark mode base ID */
  darkModeBaseId: DarkModeBaseId;
  /** Set the dark mode base by ID */
  setDarkModeBase: (baseId: DarkModeBaseId) => void;
  /** Get all available dark mode bases */
  availableDarkModeBases: DarkModeBaseDefinition[];
  /** Get current tint colors based on selected base */
  currentTintColors: DarkModeTintColors;
}

const AppThemeContext = createContext<AppThemeContextValue | undefined>(undefined);

interface AppThemeProviderProps {
  readonly children: ReactNode;
}

export function AppThemeProvider({ children }: AppThemeProviderProps): JSX.Element {
  const [themeId, setThemeId] = useState<AppThemeId>('default');
  const [styleId, setStyleId] = useState<AppStyleId>('default');
  const [darkModeTintId, setDarkModeTintId] = useState<DarkModeTintId>('medium');
  const [darkModeBaseId, setDarkModeBaseId] = useState<DarkModeBaseId>('blue'); // Default to blue-grey for subtle appearance

  // Load preferences from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem(STORAGE_KEY);
    if (savedTheme && APP_THEMES.some((t) => t.id === savedTheme)) {
      setThemeId(savedTheme as AppThemeId);
    }

    const savedStyle = localStorage.getItem(STYLE_STORAGE_KEY);
    if (savedStyle && APP_STYLES.some((s) => s.id === savedStyle)) {
      setStyleId(savedStyle as AppStyleId);
    }

    const savedTint = localStorage.getItem(DARK_MODE_TINT_STORAGE_KEY);
    if (savedTint && DARK_MODE_TINTS.some((t) => t.id === savedTint)) {
      setDarkModeTintId(savedTint as DarkModeTintId);
    }

    const savedBase = localStorage.getItem(DARK_MODE_BASE_STORAGE_KEY);
    if (savedBase && DARK_MODE_BASES.some((b) => b.id === savedBase)) {
      setDarkModeBaseId(savedBase as DarkModeBaseId);
    }
  }, []);

  const currentTheme = useMemo(() => {
    return APP_THEMES.find((t) => t.id === themeId) || DEFAULT_THEME;
  }, [themeId]);

  const currentStyle = useMemo(() => {
    return APP_STYLES.find((s) => s.id === styleId) || APP_STYLES[0];
  }, [styleId]);

  const currentDarkModeTint = useMemo(() => {
    return DARK_MODE_TINTS.find((t) => t.id === darkModeTintId) || DARK_MODE_TINTS[1]; // Default to 'medium'
  }, [darkModeTintId]);

  const currentDarkModeBase = useMemo(() => {
    return DARK_MODE_BASES.find((b) => b.id === darkModeBaseId) || DARK_MODE_BASES[1]; // Default to 'blue'
  }, [darkModeBaseId]);

  // Get the current tint colors based on the selected base
  const currentTintColors = useMemo(() => {
    return currentDarkModeTint.colors[darkModeBaseId];
  }, [currentDarkModeTint, darkModeBaseId]);

  const mantineThemeOverride = useMemo(() => {
    return generateMantineTheme(currentTheme);
  }, [currentTheme]);

  // Apply CSS custom properties for the theme
  useEffect(() => {
    const root = document.documentElement;
    // Main theme colors
    root.style.setProperty('--carepoint-app-primary', currentTheme.colors.primary[5]);
    root.style.setProperty('--carepoint-app-secondary', currentTheme.colors.secondary[5]);
    root.style.setProperty('--carepoint-app-accent', currentTheme.colors.accent[5]);
    root.style.setProperty('--carepoint-app-neutral', currentTheme.colors.neutral[5]);

    // Background colors for flat style - using neutral palette
    root.style.setProperty('--carepoint-app-bg-lightest', currentTheme.colors.neutral[0]);
    root.style.setProperty('--carepoint-app-bg-light', currentTheme.colors.neutral[1]);
    root.style.setProperty('--carepoint-app-bg-medium', currentTheme.colors.neutral[2]);

    // Chart and graph colors - primary, secondary, accent shades
    root.style.setProperty('--carepoint-chart-primary', currentTheme.colors.primary[5]);
    root.style.setProperty('--carepoint-chart-primary-light', currentTheme.colors.primary[3]);
    root.style.setProperty('--carepoint-chart-secondary', currentTheme.colors.secondary[5]);
    root.style.setProperty('--carepoint-chart-secondary-light', currentTheme.colors.secondary[3]);
    root.style.setProperty('--carepoint-chart-accent', currentTheme.colors.accent[5]);
    root.style.setProperty('--carepoint-chart-accent-light', currentTheme.colors.accent[3]);

    // Success, warning, error colors for charts
    root.style.setProperty('--carepoint-chart-success', currentTheme.colors.success[5]);
    root.style.setProperty('--carepoint-chart-warning', currentTheme.colors.warning[5]);
    root.style.setProperty('--carepoint-chart-error', currentTheme.colors.error[5]);

    // Icon wrapper colors - using theme palette shades
    root.style.setProperty('--carepoint-icon-blue-bg', currentTheme.colors.primary[0]);
    root.style.setProperty('--carepoint-icon-blue-fg', currentTheme.colors.primary[5]);
    root.style.setProperty('--carepoint-icon-teal-bg', currentTheme.colors.secondary[0]);
    root.style.setProperty('--carepoint-icon-teal-fg', currentTheme.colors.secondary[5]);
    root.style.setProperty('--carepoint-icon-accent-bg', currentTheme.colors.accent[0]);
    root.style.setProperty('--carepoint-icon-accent-fg', currentTheme.colors.accent[5]);
    root.style.setProperty('--carepoint-icon-success-bg', currentTheme.colors.success[0]);
    root.style.setProperty('--carepoint-icon-success-fg', currentTheme.colors.success[5]);
    root.style.setProperty('--carepoint-icon-warning-bg', currentTheme.colors.warning[0]);
    root.style.setProperty('--carepoint-icon-warning-fg', currentTheme.colors.warning[5]);
    root.style.setProperty('--carepoint-icon-error-bg', currentTheme.colors.error[0]);
    root.style.setProperty('--carepoint-icon-error-fg', currentTheme.colors.error[5]);
  }, [currentTheme]);

  // Apply style class to body for CSS-based flat styling
  useEffect(() => {
    const body = document.body;
    // Remove all style classes first
    body.classList.remove('app-style-default', 'app-style-flat');
    // Add the current style class
    body.classList.add(`app-style-${styleId}`);
  }, [styleId]);

  // Apply dark mode tint CSS variables (uses currentTintColors which combines tint + base)
  useEffect(() => {
    const root = document.documentElement;

    // Set CSS custom properties for dark mode tint
    root.style.setProperty('--dark-tint-9', currentTintColors.dark9);
    root.style.setProperty('--dark-tint-8', currentTintColors.dark8);
    root.style.setProperty('--dark-tint-7', currentTintColors.dark7);
    root.style.setProperty('--dark-tint-6', currentTintColors.dark6);
    root.style.setProperty('--dark-tint-5', currentTintColors.dark5);
    root.style.setProperty('--dark-tint-4', currentTintColors.dark4);
    root.style.setProperty('--dark-tint-gray-0', currentTintColors.gray0);
    root.style.setProperty('--dark-tint-gray-3', currentTintColors.gray3);
    root.style.setProperty('--dark-tint-gray-5', currentTintColors.gray5);

    // Apply tint and base classes to body for CSS-based dark mode styling
    const body = document.body;
    body.classList.remove('dark-tint-enhanced', 'dark-tint-medium', 'dark-tint-gentle');
    body.classList.add(`dark-tint-${currentDarkModeTint.id}`);
    body.classList.remove('dark-base-neutral', 'dark-base-blue');
    body.classList.add(`dark-base-${darkModeBaseId}`);
  }, [currentTintColors, currentDarkModeTint.id, darkModeBaseId]);

  const setTheme = useCallback((newThemeId: AppThemeId): void => {
    if (APP_THEMES.some((t) => t.id === newThemeId)) {
      setThemeId(newThemeId);
      localStorage.setItem(STORAGE_KEY, newThemeId);
    }
  }, []);

  const setStyle = useCallback((newStyleId: AppStyleId): void => {
    if (APP_STYLES.some((s) => s.id === newStyleId)) {
      setStyleId(newStyleId);
      localStorage.setItem(STYLE_STORAGE_KEY, newStyleId);
    }
  }, []);

  const setDarkModeTint = useCallback((newTintId: DarkModeTintId): void => {
    if (DARK_MODE_TINTS.some((t) => t.id === newTintId)) {
      setDarkModeTintId(newTintId);
      localStorage.setItem(DARK_MODE_TINT_STORAGE_KEY, newTintId);
    }
  }, []);

  const setDarkModeBase = useCallback((newBaseId: DarkModeBaseId): void => {
    if (DARK_MODE_BASES.some((b) => b.id === newBaseId)) {
      setDarkModeBaseId(newBaseId);
      localStorage.setItem(DARK_MODE_BASE_STORAGE_KEY, newBaseId);
    }
  }, []);

  const value = useMemo(
    (): AppThemeContextValue => ({
      currentTheme,
      themeId,
      setTheme,
      availableThemes: APP_THEMES,
      mantineThemeOverride,
      currentStyle,
      styleId,
      setStyle,
      availableStyles: APP_STYLES,
      currentDarkModeTint,
      darkModeTintId,
      setDarkModeTint,
      availableDarkModeTints: DARK_MODE_TINTS,
      currentDarkModeBase,
      darkModeBaseId,
      setDarkModeBase,
      availableDarkModeBases: DARK_MODE_BASES,
      currentTintColors,
    }),
    [currentTheme, themeId, setTheme, mantineThemeOverride, currentStyle, styleId, setStyle, currentDarkModeTint, darkModeTintId, setDarkModeTint, currentDarkModeBase, darkModeBaseId, setDarkModeBase, currentTintColors]
  );

  return <AppThemeContext.Provider value={value}>{children}</AppThemeContext.Provider>;
}

export function useAppTheme(): AppThemeContextValue {
  const context = useContext(AppThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within an AppThemeProvider');
  }
  return context;
}
